'use client';

import { useState } from 'react';
import { Button } from '@/shared/components/Button';
import { useAuth } from '@/features/auth';

interface SupportFormProps {
	onClose?: () => void;
}

export function SupportForm({ onClose }: SupportFormProps) {
	const { user } = useAuth();
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [subject, setSubject] = useState('');
	const [message, setMessage] = useState('');
	const [priority, setPriority] = useState<'normal' | 'high' | 'urgent'>(
		'normal'
	);
	const [category, setCategory] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		try {
			const response = await fetch('/api/support/submit', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					subject,
					message,
					priority,
					category: category || undefined,
					// Include email/name if not logged in
					...(!user && { email, name }),
				}),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || 'Failed to submit ticket');
			}

			setSubmitted(true);
			setTimeout(() => {
				onClose?.();
			}, 2000);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Failed to submit ticket'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (submitted) {
		return (
			<div className="text-center py-8">
				<div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
					<svg
						className="w-8 h-8 text-green-500"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M5 13l4 4L19 7"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-semibold mb-2">
					Ticket Submitted!
				</h3>
				<p className="text-muted-foreground">
					We&apos;ll get back to you as soon as possible.
				</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			{/* Show name/email fields only if not logged in */}
			{!user && (
				<>
					<div>
						<label
							htmlFor="name"
							className="block text-sm font-medium mb-2"
						>
							Name
						</label>
						<input
							id="name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							className="w-full px-4 py-3 rounded-lg bg-background/50 border border-white/10 focus:border-primary/50 focus:outline-none"
							placeholder="Your full name"
						/>
					</div>

					<div>
						<label
							htmlFor="email"
							className="block text-sm font-medium mb-2"
						>
							Email
						</label>
						<input
							id="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							className="w-full px-4 py-3 rounded-lg bg-background/50 border border-white/10 focus:border-primary/50 focus:outline-none"
							placeholder="your.email@example.com"
						/>
					</div>
				</>
			)}

			<div>
				<label
					htmlFor="subject"
					className="block text-sm font-medium mb-2"
				>
					Subject
				</label>
				<input
					id="subject"
					type="text"
					value={subject}
					onChange={(e) => setSubject(e.target.value)}
					required
					className="w-full px-4 py-3 rounded-lg bg-background/50 border border-white/10 focus:border-primary/50 focus:outline-none"
					placeholder="Brief description of your issue"
				/>
			</div>

			<div>
				<label
					htmlFor="category"
					className="block text-sm font-medium mb-2"
				>
					Category (Optional)
				</label>
				<select
					id="category"
					value={category}
					onChange={(e) => setCategory(e.target.value)}
					className="w-full px-4 py-3 rounded-lg bg-background/50 border border-white/10 focus:border-primary/50 focus:outline-none"
				>
					<option value="">Select a category</option>
					<option value="technical">Technical Issue</option>
					<option value="billing">Billing</option>
					<option value="feature">Feature Request</option>
					<option value="bug">Bug Report</option>
					<option value="other">Other</option>
				</select>
			</div>

			<div>
				<label
					htmlFor="priority"
					className="block text-sm font-medium mb-2"
				>
					Priority
				</label>
				<select
					id="priority"
					value={priority}
					onChange={(e) =>
						setPriority(
							e.target.value as 'normal' | 'high' | 'urgent'
						)
					}
					className="w-full px-4 py-3 rounded-lg bg-background/50 border border-white/10 focus:border-primary/50 focus:outline-none"
				>
					<option value="normal">Normal</option>
					<option value="high">High</option>
					<option value="urgent">Urgent</option>
				</select>
			</div>

			<div>
				<label
					htmlFor="message"
					className="block text-sm font-medium mb-2"
				>
					Message
				</label>
				<textarea
					id="message"
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					required
					rows={6}
					className="w-full px-4 py-3 rounded-lg bg-background/50 border border-white/10 focus:border-primary/50 focus:outline-none resize-none"
					placeholder="Describe your issue in detail..."
				/>
			</div>

			{error && (
				<div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
					{error}
				</div>
			)}

			<div className="flex gap-3">
				{onClose && (
					<Button
						type="button"
						variant="ghost"
						onClick={onClose}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
				)}
				<Button
					type="submit"
					disabled={isSubmitting}
					className="flex-1"
				>
					{isSubmitting ? 'Submitting...' : 'Submit Ticket'}
				</Button>
			</div>
		</form>
	);
}
